/**
 * Modal dialogs.
 *
 * Generic modal infrastructure plus three concrete dialogs (New Map, Add PC,
 * Add NPC). Common behavior:
 *   - ESC closes
 *   - Click on backdrop closes
 *   - Focus is moved into the dialog on open and trapped (Tab cycles inside)
 *   - On submit, the form is disabled and an error banner shows ApiError.message
 *
 * The pure FormData builders (buildNewMapForm, buildAddPcForm, buildAddNpcForm)
 * are exported so they can be unit-tested without the DOM.
 */

import * as api from './api.js';
import { addMapToList, loadMap, removeMapFromList, setState, updateMapMeta, upsertToken } from './state.js';
import { ApiError, type MapRecord } from './types.js';

// --- Pure FormData builders --------------------------------------------------

export interface NewMapFields {
  name: string;
  widthFt: number;
  heightFt: number;
  image: File;
}

export function buildNewMapForm(fields: NewMapFields): FormData {
  validatePositive('width_ft', fields.widthFt);
  validatePositive('height_ft', fields.heightFt);
  validateName(fields.name);
  const fd = new FormData();
  fd.append('name', fields.name);
  fd.append('width_ft', String(fields.widthFt));
  fd.append('height_ft', String(fields.heightFt));
  fd.append('image', fields.image);
  return fd;
}

export interface AddPcFields {
  name: string;
  visibilityFt: number;
  gridX: number;
  gridY: number;
  image: File;
  /** Defaults to false — the GM drags the token onto the map when ready. */
  isPlaced?: boolean;
}

export function buildAddPcForm(fields: AddPcFields): FormData {
  validateName(fields.name);
  validatePositive('visibility_ft', fields.visibilityFt);
  const fd = new FormData();
  fd.append('type', 'pc');
  fd.append('name', fields.name);
  fd.append('visibility_ft', String(fields.visibilityFt));
  fd.append('grid_x', String(fields.gridX));
  fd.append('grid_y', String(fields.gridY));
  fd.append('is_placed', fields.isPlaced === true ? '1' : '0');
  fd.append('image', fields.image);
  return fd;
}

export interface AddNpcFields {
  name: string;
  gridX: number;
  gridY: number;
  image: File | null;
  /** Defaults to false — the GM drags the token onto the map when ready. */
  isPlaced?: boolean;
  /** Number of identical NPC copies to create (default 1, max 20). */
  count?: number;
}

const MAX_NPC_COUNT = 20;

export function buildAddNpcForm(fields: AddNpcFields): FormData {
  validateName(fields.name);
  const count = fields.count ?? 1;
  if (!Number.isInteger(count) || count < 1 || count > MAX_NPC_COUNT) {
    throw new Error(`Count must be a whole number between 1 and ${MAX_NPC_COUNT}`);
  }
  const fd = new FormData();
  fd.append('type', 'npc');
  fd.append('name', fields.name);
  fd.append('grid_x', String(fields.gridX));
  fd.append('grid_y', String(fields.gridY));
  fd.append('is_placed', fields.isPlaced === true ? '1' : '0');
  // Only send `count` when > 1 — keeps single-create requests minimal and
  // the server's default of 1 covers the omission.
  if (count > 1) {
    fd.append('count', String(count));
  }
  if (fields.image !== null) {
    fd.append('image', fields.image);
  }
  return fd;
}

function validateName(name: string): void {
  if (name.trim() === '') throw new Error('Name is required');
  if (name.length > 255) throw new Error('Name is too long (max 255 characters)');
}

function validatePositive(field: string, value: number): void {
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error(`${field} must be greater than 0`);
  }
}

// --- Modal infrastructure ----------------------------------------------------

interface ModalElements {
  backdrop: HTMLElement;
  dialog: HTMLElement;
  form: HTMLFormElement;
  errorBox: HTMLElement;
  submitButton: HTMLButtonElement;
}

let activeModal: { teardown: () => void } | null = null;

function openModal(
  html: string,
  onSubmit: (els: ModalElements) => Promise<void>,
  onMount?: (els: ModalElements) => void,
): void {
  // Only one modal at a time.
  if (activeModal !== null) {
    activeModal.teardown();
  }

  const backdrop = document.createElement('div');
  backdrop.className = 'modal-backdrop';
  backdrop.innerHTML = html;
  document.body.appendChild(backdrop);

  const dialog = backdrop.querySelector<HTMLElement>('.modal-dialog');
  const form = backdrop.querySelector<HTMLFormElement>('.modal-form');
  const errorBox = backdrop.querySelector<HTMLElement>('.modal-error');
  const submitButton = backdrop.querySelector<HTMLButtonElement>('button[type=submit]');
  const closeButton = backdrop.querySelector<HTMLButtonElement>('.modal-close');
  if (!dialog || !form || !errorBox || !submitButton) {
    throw new Error('Modal markup is missing required elements');
  }

  const els: ModalElements = { backdrop, dialog, form, errorBox, submitButton };

  // Mount hook runs before initial focus so onMount can choose what to focus.
  onMount?.(els);

  // Move initial focus into the dialog.
  const firstField = form.querySelector<HTMLElement>('input, select, textarea, button');
  firstField?.focus();

  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      teardown();
    } else if (e.key === 'Tab') {
      trapFocus(dialog, e);
    }
  };

  const onBackdropClick = (e: MouseEvent): void => {
    if (e.target === backdrop) teardown();
  };

  const onCloseClick = (): void => {
    teardown();
  };

  const onSubmitEvt = async (e: Event): Promise<void> => {
    e.preventDefault();
    errorBox.textContent = '';
    submitButton.disabled = true;
    try {
      await onSubmit(els);
      teardown();
    } catch (err) {
      submitButton.disabled = false;
      if (err instanceof ApiError) {
        errorBox.textContent = err.message;
      } else if (err instanceof Error) {
        errorBox.textContent = err.message;
      } else {
        errorBox.textContent = 'An unexpected error occurred.';
      }
    }
  };

  document.addEventListener('keydown', onKeyDown);
  backdrop.addEventListener('click', onBackdropClick);
  closeButton?.addEventListener('click', onCloseClick);
  form.addEventListener('submit', (e) => { void onSubmitEvt(e); });

  function teardown(): void {
    document.removeEventListener('keydown', onKeyDown);
    backdrop.remove();
    activeModal = null;
  }
  activeModal = { teardown };
}

function trapFocus(container: HTMLElement, e: KeyboardEvent): void {
  const focusables = container.querySelectorAll<HTMLElement>(
    'input, select, textarea, button, [tabindex]:not([tabindex="-1"])',
  );
  if (focusables.length === 0) return;
  const first = focusables[0]!;
  const last = focusables[focusables.length - 1]!;
  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
}

// --- Concrete modals ---------------------------------------------------------

/** Open the "New Map" modal. Resolves when the user submits or cancels. */
export function openNewMapModal(): void {
  openModal(`
    <div class="modal-dialog" role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <header class="modal-header">
        <h2 id="modal-title">New map</h2>
        <button type="button" class="modal-close" aria-label="Close">×</button>
      </header>
      <form class="modal-form" novalidate>
        <label>
          <span>Name</span>
          <input type="text" name="name" required maxlength="255" />
        </label>
        <label>
          <span>Background image</span>
          <input type="file" name="image" accept="image/*" required />
        </label>
        <div class="modal-row">
          <label>
            <span>Width (ft)</span>
            <input type="number" name="width_ft" min="1" step="any" required />
          </label>
          <label>
            <span>Height (ft)</span>
            <input type="number" name="height_ft" min="1" step="any" required />
          </label>
        </div>
        <div class="modal-error" role="alert"></div>
        <footer class="modal-footer">
          <button type="submit">Create map</button>
        </footer>
      </form>
    </div>
  `, async (els) => {
    const data = new FormData(els.form);
    const file = data.get('image');
    if (!(file instanceof File) || file.size === 0) throw new Error('Image is required');

    const fd = buildNewMapForm({
      name: String(data.get('name') ?? ''),
      widthFt: Number(data.get('width_ft')),
      heightFt: Number(data.get('height_ft')),
      image: file,
    });
    const map: MapRecord = await api.createMap(fd);
    addMapToList(map);
    await loadMap(map.id);
  });
}

/** Open the "Add PC" modal. Token is placed at (0,0) by default — drag it from the legend onto the canvas to position. */
export function openAddPcModal(mapId: number): void {
  openModal(`
    <div class="modal-dialog" role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <header class="modal-header">
        <h2 id="modal-title">Add player character</h2>
        <button type="button" class="modal-close" aria-label="Close">×</button>
      </header>
      <form class="modal-form" novalidate>
        <label>
          <span>Name</span>
          <input type="text" name="name" required maxlength="255" />
        </label>
        <label>
          <span>Portrait</span>
          <input type="file" name="image" accept="image/*" required />
        </label>
        <label>
          <span>Visibility (ft)</span>
          <input type="number" name="visibility_ft" min="1" step="1" value="30" required />
        </label>
        <div class="modal-error" role="alert"></div>
        <footer class="modal-footer">
          <button type="submit">Add PC</button>
        </footer>
      </form>
    </div>
  `, async (els) => {
    const data = new FormData(els.form);
    const file = data.get('image');
    if (!(file instanceof File) || file.size === 0) throw new Error('Portrait is required');

    const fd = buildAddPcForm({
      name: String(data.get('name') ?? ''),
      visibilityFt: Number(data.get('visibility_ft')),
      gridX: 0,
      gridY: 0,
      image: file,
    });
    // The endpoint always returns an array; PC creation is single, so one entry.
    const tokens = await api.createTokens(mapId, fd);
    tokens.forEach(upsertToken);
  });
}

/**
 * Open the "Add NPC" modal. Image is optional — if omitted, frontend uses
 * the default silhouette. The Count field creates N identical tokens at once,
 * named "<Name> 1" … "<Name> N" (or continuing past existing numbered
 * siblings on the same map — see backend pickBatchNames).
 */
export function openAddNpcModal(mapId: number): void {
  openModal(`
    <div class="modal-dialog" role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <header class="modal-header">
        <h2 id="modal-title">Add NPC</h2>
        <button type="button" class="modal-close" aria-label="Close">×</button>
      </header>
      <form class="modal-form" novalidate>
        <div class="modal-row">
          <label>
            <span>Name</span>
            <input type="text" name="name" required maxlength="255" />
          </label>
          <label>
            <span>Count</span>
            <input type="number" name="count" min="1" max="${MAX_NPC_COUNT}" step="1" value="1" required />
          </label>
        </div>
        <label>
          <span>Portrait <span class="optional">(optional)</span></span>
          <input type="file" name="image" accept="image/*" />
        </label>
        <div class="modal-error" role="alert"></div>
        <footer class="modal-footer">
          <button type="submit">Add NPC</button>
        </footer>
      </form>
    </div>
  `, async (els) => {
    const data = new FormData(els.form);
    const fileEntry = data.get('image');
    const image = fileEntry instanceof File && fileEntry.size > 0 ? fileEntry : null;

    const fd = buildAddNpcForm({
      name: String(data.get('name') ?? ''),
      gridX: 0,
      gridY: 0,
      image,
      count: Number(data.get('count') ?? 1),
    });
    const tokens = await api.createTokens(mapId, fd);
    tokens.forEach(upsertToken);
  });
}

/**
 * Confirm-then-delete modal for the currently-loaded map.
 * On confirm: DELETE /api/maps/:id, remove from state, auto-load the next
 * remaining map (if any), and exit overlay-edit mode.
 *
 * The map name is set via textContent (not innerHTML) to avoid injection
 * from user-supplied names.
 */
export function openDeleteMapModal(map: Pick<MapRecord, 'id' | 'name'>): void {
  openModal(`
    <div class="modal-dialog" role="alertdialog" aria-modal="true" aria-labelledby="modal-title">
      <header class="modal-header">
        <h2 id="modal-title">Delete map</h2>
        <button type="button" class="modal-close" aria-label="Close">×</button>
      </header>
      <form class="modal-form" novalidate>
        <p>
          This permanently deletes the map
          <strong class="delete-target"></strong>
          and all of its tokens, walls, and uploaded images. This cannot be undone.
        </p>
        <div class="modal-error" role="alert"></div>
        <footer class="modal-footer">
          <button type="button" class="modal-cancel">Cancel</button>
          <button type="submit" class="btn-danger">Delete map</button>
        </footer>
      </form>
    </div>
  `, async (_els) => {
    await api.deleteMap(map.id);
    removeMapFromList(map.id);

    // Clear the view; reset edit mode so we're not stuck painting a missing map.
    // No auto-load — the GM picks the next map explicitly via the selector.
    setState({
      currentMap: null,
      tokens: [],
      viewport: { x: 0, y: 0, zoom: 1 },
      editMode: 'tokens',
    });
  }, (els) => {
    // Set the map name safely via textContent.
    const target = els.dialog.querySelector<HTMLElement>('.delete-target');
    if (target !== null) target.textContent = `"${map.name}"`;
    wireCancelButton(els);
  });
}

/** Wire a `.modal-cancel` button to proxy to the close button so teardown runs. */
function wireCancelButton(els: ModalElements): void {
  const cancelBtn = els.dialog.querySelector<HTMLButtonElement>('.modal-cancel');
  cancelBtn?.addEventListener('click', () => {
    els.dialog.querySelector<HTMLButtonElement>('.modal-close')?.click();
  });
}

/**
 * Rename modal for the currently-loaded map.
 * Pre-fills with the current name. On submit: PUT /api/maps/:id with the new
 * name, then update both `currentMap` and the matching entry in `maps` so the
 * toolbar label and the selector dropdown reflect the change.
 */
export function openRenameMapModal(map: Pick<MapRecord, 'id' | 'name'>): void {
  openModal(`
    <div class="modal-dialog" role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <header class="modal-header">
        <h2 id="modal-title">Rename map</h2>
        <button type="button" class="modal-close" aria-label="Close">×</button>
      </header>
      <form class="modal-form" novalidate>
        <label>
          <span>Name</span>
          <input type="text" name="name" required maxlength="255" />
        </label>
        <div class="modal-error" role="alert"></div>
        <footer class="modal-footer">
          <button type="button" class="modal-cancel">Cancel</button>
          <button type="submit">Save</button>
        </footer>
      </form>
    </div>
  `, async (els) => {
    const data = new FormData(els.form);
    const newName = String(data.get('name') ?? '').trim();
    if (newName === '') throw new Error('Name is required');
    if (newName === map.name) return; // No-op; close cleanly.

    const updated = await api.renameMap(map.id, newName);
    updateMapMeta(updated);
  }, (els) => {
    // Pre-fill the input with the current name so the GM can tweak it.
    // Done via DOM (not interpolated into the template) to dodge HTML injection
    // from quote / angle-bracket characters in user-supplied names.
    const nameInput = els.form.querySelector<HTMLInputElement>('input[name=name]');
    if (nameInput !== null) {
      nameInput.value = map.name;
      nameInput.select();
    }
    wireCancelButton(els);
  });
}
